import { OrderDetailDto } from '@/services/platform/api/model';
import { cn, useClientTranslation, useLocaleStore } from '@/lib/core';
import { formatAmount } from '@/lib/core/utils/currency';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { formatActivityDate } from '@/lib/core/utils/dateTime';
import { getActivityStatusColor } from '@/lib/core/utils/activityStatus';
import { ActivityInvoiceButton } from './invoice';

function formatStageCode(code: string): string {
  return code
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

interface ActivityOrderProps {
  order: OrderDetailDto;
  name?: string | null;
}

export function ActivityOrder({ order, name }: ActivityOrderProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const isDelivered = ['DELIVERED', 'COMPLETED'].includes(order.status?.toUpperCase() ?? '');
  const hasPriceSummary = order.subtotal != null || order.totalAmount != null;

  const stageMeta: Record<
    string,
    {
      label: string;
      desc: string;
      info?: string | undefined;
    }
  > = {
    ORDER_PLACED: {
      label: t('App.Labels.ActivityStageOrderPlaced'),
      desc: t('App.Labels.ActivityStageOrderPlacedDesc'),
      info: undefined,
    },
    PLACED: {
      label: t('App.Labels.ActivityStageOrderPlaced'),
      desc: t('App.Labels.ActivityStageOrderPlacedDesc'),
      info: undefined,
    },
    PAYMENTCONFIRMED: {
      label: t('App.Labels.ActivityStagePaymentConfirmed'),
      desc: t('App.Labels.ActivityStagePaymentConfirmedDesc'),
      info: undefined,
    },
    PROCESSING: {
      label: t('App.Labels.ActivityStageOrderProcessing'),
      desc: t('App.Labels.ActivityStageOrderProcessingDesc'),
      info: t('App.Labels.ActivityStageShippedInfo'),
    },
    SHIPPED: {
      label: t('App.Labels.ActivityStageShipped'),
      desc: t('App.Labels.ActivityStageShippedDesc'),
      info: t('App.Labels.ActivityStageShippedInfo'),
    },
    DELIVERED: {
      label: t('App.Labels.ActivityStageDelivered'),
      desc: t('App.Labels.ActivityStageDeliveredDesc'),
      info: undefined,
    },
  };

  const activeStageInfo = (() => {
    if (!order.timeline?.length) return undefined;
    const lastCompleted = [...order.timeline].reverse().find(s => s.completed);
    const code = lastCompleted?.stageCode?.toUpperCase();
    return code ? stageMeta[code]?.info : undefined;
  })();

  return (
    <div className="flex flex-col gap-10 pb-8">
      {/* top info */}
      <div className="flex flex-col gap-8 pb-8">
        <div className="flex flex-col gap-2">
          {/* category · status */}
          <div className="flex items-center gap-2.5">
            {order.category && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] uppercase">
                {order.category}
              </span>
            )}
            {order.category && order.status && <span className="text-jncquoi-neutral-500 text-[4px]">●</span>}
            {order.status && (
              <span
                className={cn(
                  'font-pp-neue-montreal shrink-0 text-[10px] font-semibold tracking-[0.5px] uppercase',
                  getActivityStatusColor(order.status)
                )}
              >
                {order.status}
              </span>
            )}
          </div>
          {/* title */}
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 overflow-hidden text-[24px] font-semibold text-ellipsis">
            {name ?? order.orderNumber}
          </p>
          {/* status banner */}
          {(activeStageInfo || (isDelivered && (order.endDate ?? order.effectiveDate))) && (
            <div className="mt-2 flex flex-col gap-2 bg-[#f4f4f4] p-4">
              {activeStageInfo && (
                <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px]">
                  {activeStageInfo}
                </p>
              )}
              {isDelivered && (order.endDate ?? order.effectiveDate) && (
                <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px]">
                  {t('App.Labels.ActivityDetailReturnUntil', {
                    date: formatActivityDate(order.endDate ?? order.effectiveDate, locale),
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* order number */}
        <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-l-[0.5px] pl-4">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] font-semibold tracking-[0.5px]">
            {t('App.Labels.ActivityDetailOrderNumber')}
          </p>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] tracking-[0.5px]">
            {order.orderNumber ?? '—'}
          </p>
        </div>

        {/* ship to + order date */}
        <div className="flex gap-6">
          {order.venueName && (
            <div className="border-jncquoi-neutral-200 flex min-w-0 flex-1 flex-col gap-2 border-l-[0.5px] pl-4">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
                {t('App.Labels.ActivityDetailShipTo')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                {order.venueName}
              </p>
            </div>
          )}
          {order.effectiveDate && (
            <div className="border-jncquoi-neutral-200 flex min-w-0 flex-1 flex-col gap-2 border-l-[0.5px] pl-4">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
                {t('App.Labels.ActivityDetailOrderDate')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                {formatActivityDate(order.effectiveDate, locale)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* order status stepper */}
      {order.timeline && order.timeline.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
            {t('App.Labels.ActivityDetailOrderStatus')}
          </p>
          <div className="flex flex-col">
            {order.timeline.map((stage, i) => {
              const isLast = i === order.timeline!.length - 1;
              const code = stage.stageCode?.toUpperCase() ?? '';
              const meta = stageMeta[code] ?? { label: formatStageCode(stage.stageCode ?? ''), desc: '' };
              return (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex shrink-0 flex-col items-center pt-2">
                    <div
                      className={cn(
                        'size-2 shrink-0 rounded-full',
                        stage.completed ? 'bg-[#1a1a1a]' : 'bg-[#bababa]'
                      )}
                    />
                    {!isLast && (
                      <div
                        className={cn('mt-2 h-10 w-px', stage.completed ? 'bg-[#1a1a1a]' : 'bg-[#bababa]')}
                      />
                    )}
                  </div>
                  <div
                    className={cn(
                      'flex flex-col gap-1 pt-0',
                      stage.completed ? 'text-jncquoi-neutral-1000' : 'text-jncquoi-neutral-700'
                    )}
                  >
                    <p className="text-[14px] font-medium tracking-[0.5px]">{meta.label}</p>
                    {meta.desc && (
                      <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px]">{meta.desc}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* request return / exchange — delivered */}
      {isDelivered && (
        <div className="flex flex-col gap-2">
          <Button variant="secondary-outline" palette="dark" className="w-full">
            {t('App.Labels.ActivityDetailRequestReturn')}
          </Button>
          <Button variant="secondary-outline" palette="dark" className="w-full">
            {t('App.Labels.ActivityDetailRequestExchange')}
          </Button>
        </div>
      )}

      {/* items expandable */}
      {order.items && order.items.length > 0 && (
        <Accordion type="single" collapsible defaultValue="items">
          <AccordionItem value="items" className="border-b-[0.5px] border-[#4e4e4e]">
            <AccordionTrigger>
              {t('App.Labels.ActivityDetailViewItems', { count: order.items.length })}
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2.5 pb-4">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  {item.imageUrl && (
                    <div className="relative h-[72px] w-[50px] shrink-0 overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.name ?? ''}
                        className="absolute inset-0 size-full max-w-none object-cover"
                      />
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex flex-col gap-2">
                      {item.sku && (
                        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 truncate text-[10px] font-semibold tracking-[0.5px] uppercase">
                          {item.sku}
                        </p>
                      )}
                      <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 max-h-7 overflow-hidden text-[12px] tracking-[0.5px] text-ellipsis">
                        {item.name}
                      </p>
                    </div>
                    <div className="font-pp-neue-montreal flex flex-wrap items-center gap-2">
                      {item.variantInfo && (
                        <>
                          <span className="text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] whitespace-nowrap">
                            {item.variantInfo}
                          </span>
                          <span className="text-jncquoi-neutral-500 text-[4px]">●</span>
                        </>
                      )}
                      <span className="text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] whitespace-nowrap">
                        {t('App.Labels.ActivityDetailQty')} {item.quantity}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* price summary */}
      {hasPriceSummary && (
        <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pb-4">
          {order.subtotal != null && (
            <div className="flex items-center gap-4">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 flex-1 text-[14px] tracking-[0.5px]">
                {t('App.Labels.ActivityDetailSubtotal')}
              </span>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                {formatAmount(order.subtotal, order.currencyCode, locale)}
              </span>
            </div>
          )}
          {order.shippingAmount != null && (
            <div className="flex items-center gap-4">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 flex-1 text-[14px] tracking-[0.5px]">
                {t('App.Labels.ActivityDetailShipping')}
              </span>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                {formatAmount(order.shippingAmount, order.currencyCode, locale)}
              </span>
            </div>
          )}
          {order.discountAmount != null && (
            <div className="flex items-center gap-4">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 flex-1 text-[14px] tracking-[0.5px]">
                {t('App.Labels.ActivityDetailAppliedDiscount')}
              </span>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                {formatAmount(order.discountAmount, order.currencyCode, locale)}
              </span>
            </div>
          )}
          {order.totalAmount != null && (
            <div className="flex items-center gap-4">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[14px] font-semibold tracking-[0.5px]">
                {t('App.Labels.ActivityDetailTotal')}
              </span>
              <span className="text-jncquoi-neutral-1000 text-[16px] font-semibold tracking-[0.25px] whitespace-nowrap">
                {formatAmount(order.totalAmount, order.currencyCode, locale)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* invoice download — renders only when the order carries one */}
      <ActivityInvoiceButton orderId={order.publicGuid} invoice={order.invoice} />
    </div>
  );
}
